import CardFixed from "components/Card/CardFixed";
import { SubLine, calcMonthDiff, restEmpty, simpleAlert } from "components/CommonLib/CommonLib";
import { AUTH_CODES } from "components/CommonLib/Consts";
import Loading from "components/Loading/Loading/Loading";
import NormalBtn from "components/NewButton/NormalBtn";
import { useGetBranchAccessPermissions } from "hooks/apis/auth/usePermissions";
import { useVaultCashHist } from "hooks/apis/salary/useVaultCash";
import { useVaultCashBalance } from "hooks/apis/salary/useVaultCashBalance";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { exportToExcel } from "./makeExcel";
import { useVaultCashStore } from "./modules/store/vaultCashState";
import SearchCondition from "./SearchCondition";
import VaultCashResultTable from "./VaultCashResultTable";

const VaultCashView = () => {
  const storesDispatch = useDispatch();
  // 전역 상태 관리
  const {
    vaultCashHQBranchCombo: hqBranchCombo,
    vaultCashSearchParams: searchParams,
    setSearchParams,
    setVaultCashHQBranchCombo: setHqBranchCombo,
    resetSearchParams,
  } = useVaultCashStore();

  // 페이지 상태 관리
  const [hqBranchData, setHqBranchData] = useState(_.cloneDeep(hqBranchCombo));
  const [searchResult, setSearchResult] = useState([]);
  const [totalPage, setTotalPage] = useState(10);
  const [resetFlag, setResetFlag] = useState(false);
  const [isExcel, setIsExcel] = useState(false);
  const [vaultCashBalance, setVaultCashBalance] = useState(0);
  const isFirstLoad = useRef(true);

  // API 상태 관리
  const [isGetVaultCashHist, setIsGetVaultCashHist] = useState(false);
  const cashHistQueryData = { searchParams, isExcel };
  const cashBalanceQueryData = { branchId: hqBranchCombo.selected_branch_info.branch_id };

  // 본부 및 지점 권한 Combo 가져오기
  const { data: branchAccessData } = useGetBranchAccessPermissions(AUTH_CODES.VAULT_CASH, true);
  // 시재금 관리 이력
  const { data: vaultCashHistData, isFetching } = useVaultCashHist(
    cashHistQueryData,
    isGetVaultCashHist,
    setIsGetVaultCashHist,
  );
  const { data: vaultCashBalanceData } = useVaultCashBalance(cashBalanceQueryData);

  // 시재금 관리 리스트 목록 검색
  const onClickSearch = async (page, isExcel) => {
    if (isExcel) {
      setIsExcel(true);
    }
    setSearchParams("page_number", page);
    setIsGetVaultCashHist(true);
  };

  // 엑셀 다운로드
  const onClickExcelDownload = () => {
    if (searchParams.start_date === "" || searchParams.end_date === "") {
      simpleAlert(storesDispatch, "알림", "시작일과 종료일을 입력해주세요.");
      return;
    }
    if (calcMonthDiff(searchParams.start_date, searchParams.end_date, 3)) {
      simpleAlert(
        storesDispatch,
        "알림",
        "자료 다운로드 시\n최대 3개월 이내로 기간을 설정해주시기 바랍니다",
      );
      return;
    }

    simpleAlert(
      storesDispatch,
      "알림",
      `"${hqBranchCombo.selected_branch_info.branch_name}_시재금_이력.xlsx"\n엑셀 다운로드 하시겠습니까?`,
      false,
      undefined,
      () => {
        onClickSearch(1, true);
      },
      () => {},
    );
  };

  // API 받아온 데이터 세팅
  useEffect(() => {
    // 시재금 데이터 세팅
    if (vaultCashHistData && !restEmpty(vaultCashHistData.data.data)) {
      if (isExcel) {
        if (vaultCashHistData.data.data.length === 0) {
          setIsExcel(false);
          simpleAlert(storesDispatch, "알림", "데이터가 없습니다.");
          return;
        }
        exportToExcel(
          vaultCashHistData.data.data,
          `${hqBranchCombo.selected_branch_info.branch_name}_시재금_이력`,
        );
        setIsExcel(false);
      } else {
        setSearchResult(vaultCashHistData.data.data);
        setTotalPage(vaultCashHistData.data.total_page);
        // 현재 시재금 잔액
        if (hqBranchCombo.selected_branch_info.branch_name === "전체") {
          setVaultCashBalance(null);
        } else {
          setVaultCashBalance(vaultCashBalanceData.data.data.vault_cash);
        }
      }
    }
    // 지점 접근 권한 데이터 세팅
    if (branchAccessData && !restEmpty(branchAccessData.data)) {
      if (branchAccessData.data.length > 0) {
        setHqBranchData(branchAccessData);
        if (restEmpty(searchParams.hq_ids[0])) {
          setHqBranchCombo("selected_hq_info", branchAccessData.data[0].hq_info);
          setHqBranchCombo("selected_branch_info", branchAccessData.data[0].branch_list[0]);
          setHqBranchCombo("hq_list", branchAccessData.hq_list);
        }
      }
    }
  }, [vaultCashHistData, branchAccessData]);

  // 첫 페이지 진입 시 검색 결과를 가져오기, 콤보 데이터 먼저 받아오고 가져오기
  useEffect(() => {
    if (isFirstLoad.current && branchAccessData) {
      setIsGetVaultCashHist(true);
      isFirstLoad.current = false;
    }
  }, [branchAccessData]);

  return (
    <CardFixed
      midDom={
        <div>
          {isFetching && <Loading />}

          <div>
            <SearchCondition
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              hqBranchData={hqBranchData}
              hqBranchCombo={hqBranchCombo}
              setHqBranchCombo={setHqBranchCombo}
              resetFlag={resetFlag}
              onClickSearch={onClickSearch}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "0.75rem",
                gap: "0.3rem",
              }}
            >
              <NormalBtn
                name="초기화"
                theme="white"
                width="6.25rem"
                onClick={() => {
                  resetSearchParams();
                  setResetFlag(!resetFlag);
                }}
              />
              <NormalBtn
                name="검색"
                width="6.25rem"
                onClick={() => {
                  onClickSearch(1);
                }}
              />
            </div>
          </div>

          <SubLine />

          <div style={{ marginTop: "1rem" }}>
            <VaultCashResultTable
              searchResult={searchResult}
              curPage={searchParams.page_number}
              totalPage={totalPage}
              onClickSearch={onClickSearch}
              onClickExcelDownload={onClickExcelDownload}
              hqBranchCombo={hqBranchCombo}
              vaultCashBalance={vaultCashBalance}
            />
          </div>
        </div>
      }
      isLoading={false}
      rootStyle={{}}
    />
  );
};
export default VaultCashView;
